/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';

import hasRepresentantsLegaux from '../hasRepresentantsLegaux';

/**
 * Message porté par le nom et le prénom du représentant légal 1, seul
 * représentant obligatoire (c'est lui qui porte l'astérisque dans l'étape).
 *
 * La règle ne se déclenche que si `hasRepresentantsLegaux` est vrai, cas dans
 * lequel l'étape affiche déjà « Représentant légal 1 » / « Représentant légal 2 ».
 */
export const MESSAGE_REPRESENTANT_LEGAL_REQUIS =
    'Le nom et le prénom du représentant légal 1 sont obligatoires';

/**
 * Ne signale que les champs d'identité réellement manquants du représentant :
 * inutile de marquer en rouge un nom déjà saisi. `_.trim` absorbe en une passe
 * `null`, `undefined`, `''` et les espaces seuls (l'UI écrit `''` quand on
 * vide un champ, cf. useNomFamilleField).
 */
const champsIdentiteManquants = (parent: any) => {
    const errors = {
        nomFamille: '',
        prenom: '',
    };

    if (_.isEmpty(_.trim(parent?.nomFamille))) {
        errors.nomFamille = MESSAGE_REPRESENTANT_LEGAL_REQUIS;
    }

    if (_.isEmpty(_.trim(parent?.prenom))) {
        errors.prenom = MESSAGE_REPRESENTANT_LEGAL_REQUIS;
    }

    return _.pickBy(errors, (value) => value !== '');
};

/**
 * Le libellé de la commune déléguée de naissance est obligatoire dès que la
 * case est cochée. Contrairement à `champsIdentiteManquants`, cette règle
 * s'applique toujours : le champ est affiché pour chaque parent/représentant,
 * indépendamment des représentants légaux.
 */
const communeDelegueManquante = (parent: any) => {
    const errors = { naissanceLibelleCommuneDelegue: '' };

    if (
        parent?.adresseNaissance?.naissanceCommuneDelegue
        && _.isEmpty(_.trim(parent?.adresseNaissance?.naissanceLibelleCommuneDelegue))
    ) {
        errors.naissanceLibelleCommuneDelegue = 'Le libellé de la commune déléguée de naissance est obligatoire';
    }

    return _.pickBy(errors, (value) => value !== '');
};

/**
 * Lorsqu'un partenaire est sous mesure de protection juridique ou sous la
 * protection de l'OFPRA, ses « parents » sont ses représentants légaux : le
 * représentant légal 1 doit être identifié (nom de famille + prénom), le
 * représentant légal 2 reste facultatif. Facultatif aussi pour un projet de
 * PACS : la règle ne vaut que pour les déclarations initiale et modificative.
 *
 * Les clés retournées correspondent aux deux colonnes de `StepParentsPartenaire` :
 * `parent1` = `perePartenaire{N}`, `parent2` = `merePartenaire{N}`.
 *
 * `filiationInconnue{Mere,Pere}Partenaire{N}` n'est pas atteignable en PACS
 * (la case est conditionnée à `isNotPACS` dans EtatCivil) : pas de
 * court-circuit ici, contrairement à `validationInformationsNaissance`.
 */
const validationParentsPartenaire = (
    data: any,
    numeroPartenaire: number,
    selectedDeclarationType?: string | null,
) => {
    const conventionDeclaration = data?.conventionDeclaration;
    const partenaire = conventionDeclaration?.[`partenaire${numeroPartenaire}`];
    const parent1 = conventionDeclaration?.[`perePartenaire${numeroPartenaire}`];
    const parent2 = conventionDeclaration?.[`merePartenaire${numeroPartenaire}`];

    // Commune déléguée de naissance : s'applique toujours, que le partenaire
    // ait ou non des représentants légaux (le champ est affiché dans les
    // deux cas).
    const errorsParent1 = communeDelegueManquante(parent1);
    const errorsParent2 = communeDelegueManquante(parent2);

    // Identité du représentant légal 1 : uniquement si le partenaire a des
    // représentants légaux (protection juridique ou OFPRA), hors projet.
    if (selectedDeclarationType !== 'projet' && hasRepresentantsLegaux(partenaire)) {
        Object.assign(errorsParent1, champsIdentiteManquants(parent1));
    }

    return _.pickBy(
        { parent1: errorsParent1, parent2: errorsParent2 },
        (value) => !_.isEmpty(value),
    );
};

export default validationParentsPartenaire;
